/**
 * D-1023-BUSY — one pattern for every press that calls the server (Eli, 23 Oct 2026: "I click Save, Continue, Add… I
 * never know when something worked or not. From the moment I click, if it's loading it needs to say Saving, Adding —
 * whatever it's doing. A button just sitting there makes you keep clicking.").
 *
 * From the click until the server answers the button: says what it is doing in words ("Saving…", "Adding…", in the
 * language of the page), shows a small spinner (decorative), says `aria-busy`, ignores a second press, and the live
 * region says the busy words once. When it answers, the button shows the done state ("Added ✓") for a moment where it
 * stays on screen. After 10 seconds it says "Still working…". On a refusal it comes back and says what happened.
 *
 * `usePress` is the engine; `<Button>` uses it for any `onClick` that returns a promise (and takes `busy` for presses
 * a page tracks itself); a menu item or a link-looking control uses `usePress` directly.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import i18n from 'i18next';
import { useAnnounceSafe } from '@/components/Announcer';
import { PressRefused } from './press-track';

export type PressPhase = 'idle' | 'busy' | 'still' | 'done';

/** The words of a label (English or Spanish) that mean a verb we can say as it happens. */
export const VERB_IDS: Record<string, string> = {
  save: 'save', guardar: 'save',
  continue: 'continue', continuar: 'continue',
  add: 'add', agregar: 'add', añadir: 'add',
  send: 'send', enviar: 'send', resend: 'send', reenviar: 'send',
  book: 'book', reservar: 'book',
  move: 'move', mover: 'move',
  remove: 'remove', quitar: 'remove', eliminar: 'remove',
  approve: 'approve', aprobar: 'approve',
  confirm: 'confirm', confirmar: 'confirm',
  upload: 'upload', subir: 'upload', cargar: 'upload',
  check: 'check', comprobar: 'check', 'check-in': 'checkin', registrar: 'record', record: 'record',
  finish: 'finish', finalizar: 'finish', terminar: 'finish',
  cancel: 'cancel', cancelar: 'cancel',
  create: 'create', crear: 'create',
  apply: 'apply', aplicar: 'apply',
  submit: 'submit',
  change: 'change', cambiar: 'change',
  update: 'update', actualizar: 'update',
  give: 'give', dar: 'give',
  draw: 'draw', sortear: 'draw',
  release: 'release', liberar: 'release',
  pause: 'pause', pausar: 'pause',
  resume: 'resume', reanudar: 'resume',
  restore: 'restore', restaurar: 'restore',
  assign: 'assign', asignar: 'assign',
  accept: 'accept', aceptar: 'accept',
  decline: 'decline', rechazar: 'decline',
  reschedule: 'reschedule', reprogramar: 'reschedule',
  import: 'import', importar: 'import',
  void: 'void', anular: 'void',
  return: 'return', devolver: 'return',
  reopen: 'reopen', reabrir: 'reopen',
  mark: 'mark', marcar: 'mark',
  invite: 'invite', invitar: 'invite',
  queue: 'queue',
  schedule: 'schedule', programar: 'schedule',
  request: 'request', solicitar: 'request',
  sign: 'sign', firmar: 'sign',
  join: 'join', unirse: 'join',
  yes: 'confirm', sí: 'confirm', si: 'confirm',
  delete: 'remove', borrar: 'remove',
  set: 'save', establecer: 'save',
  stop: 'stop', detener: 'stop',
  start: 'start', iniciar: 'start', empezar: 'start',
  edit: 'save',
  retry: 'retry', reintentar: 'retry',
  run: 'run', ejecutar: 'run',
  review: 'review', revisar: 'review',
  swap: 'move', reorder: 'move',
  withdraw: 'withdraw', retirar: 'withdraw',
  clear: 'clear', limpiar: 'clear',
  reset: 'reset', restablecer: 'reset',
  sync: 'sync', sincronizar: 'sync',
  duplicate: 'create', duplicar: 'create',
  copy: 'copy', copiar: 'copy',
  print: 'print', imprimir: 'print',
  download: 'download', descargar: 'download',
  open: 'open', abrir: 'open',
  close: 'close', cerrar: 'close',
  done: 'save', listo: 'save', hecho: 'save',
  skip: 'skip', omitir: 'skip',
  next: 'continue', siguiente: 'continue',
  turn: 'save', activar: 'save', desactivar: 'save',
  ask: 'ask', preguntar: 'ask',
  translate: 'translate', traducir: 'translate',
  call: 'call', llamar: 'call', log: 'record',
  take: 'confirm', tomar: 'confirm',
};

/** English fallback for a page with no i18n loaded (a unit test of a bare Button). */
const EN: Record<string, [string, string]> = {
  save: ['Saving…', 'Saved'], add: ['Adding…', 'Added'], send: ['Sending…', 'Sent'], book: ['Booking…', 'Booked'],
  move: ['Moving…', 'Moved'], remove: ['Removing…', 'Removed'], approve: ['Approving…', 'Approved'], confirm: ['Confirming…', 'Confirmed'],
  upload: ['Uploading…', 'Uploaded'], checkin: ['Checking in…', 'Checked in'], check: ['Checking…', 'Checked'], continue: ['Continuing…', 'Done'], finish: ['Finishing…', 'Finished'],
  record: ['Recording…', 'Recorded'], cancel: ['Cancelling…', 'Cancelled'],
};

function say(key: string, fallback: string, opts?: Record<string, unknown>): string {
  if (!i18n.isInitialized) return fallback.replace(/\{\{(\w+)\}\}/g, (_m, k: string) => String(opts?.[k] ?? ''));
  const v = i18n.t(key, { defaultValue: fallback, ...opts });
  return typeof v === 'string' ? v : fallback;
}

/** The plain text of a label (icons and spacing dropped). */
export function textOf(node: unknown): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join(' ');
  const props = (node as { props?: { children?: unknown } }).props;
  return props ? textOf(props.children) : '';
}

/** "Add people" → { busy: "Adding…", done: "Added" }; a label with no verb we know → "Working…". */
export function verbsFor(label: string): { busy: string; done: string } {
  const words = label.trim().toLowerCase().replace(/[…!.:,;]+$/u, '').split(/\s+/);
  const id = /^check[- ]?in$/.test(`${words[0]} ${words[1] ?? ''}`.trim()) || /^check-?in$/.test(words[0] ?? '') ? 'checkin' : VERB_IDS[words[0] ?? ''];
  if (!id) return { busy: say('busy1023.working', 'Working…'), done: say('busy1023.doneGeneric', 'Done') };
  const en = EN[id];
  return { busy: say(`busy1023.ing.${id}`, en?.[0] ?? 'Working…'), done: say(`busy1023.done.${id}`, en?.[1] ?? 'Done') };
}

export const STILL_MS = 10_000;
export const DONE_MS = 1_500;

export interface PressOptions {
  /** Said while the press is out; default is derived from `label`. */
  busyLabel?: string;
  /** Shown (with a check) for a moment when it answers; default is derived from `label`. */
  doneLabel?: string;
  /** The words of the button, to derive the verb from. */
  label?: string;
  /** Say the done words in the live region (off when the caller already says its own outcome). */
  announceDone?: boolean;
  /** Wait this long before the busy look and the announcement (a handler that is only asking a question and sends nothing never shows one). */
  graceMs?: number;
  /** Keep the done state on screen (off for a button that is gone when the work is done). */
  showDone?: boolean | (() => boolean);
}

export interface PressState {
  phase: PressPhase;
  /** True from the click to the answer. */
  busy: boolean;
  /** The words for the current phase (busy / still / done), or '' when idle. */
  text: string;
  /** True from the instant a press starts (before React has drawn it) — a second click in the same breath is ignored with this. */
  flying: () => boolean;
  /** Run one press. A second call while one is out is ignored (returns undefined). A refusal is said, not thrown. */
  run: <T>(fn: () => Promise<T> | T, o?: PressOptions) => Promise<T | undefined>;
}

const errorWords = (err: unknown): string => (err instanceof Error && err.message ? err.message : say('busy1023.failedGeneric', 'It did not work.'));

export function usePress(defaults: PressOptions = {}): PressState {
  const announce = useAnnounceSafe();
  const [phase, setPhase] = useState<PressPhase>('idle');
  const [text, setText] = useState('');
  const flying = useRef(false);
  const alive = useRef(true);
  const timers = useRef<number[]>([]);
  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      timers.current.forEach((x) => window.clearTimeout(x));
    };
  }, []);

  const run = useCallback(
    async <T,>(fn: () => Promise<T> | T, o: PressOptions = {}): Promise<T | undefined> => {
      if (flying.current) return undefined; // the handler ignores a second press while one is out
      const opt = { ...defaultsRef.current, ...o };
      const verbs = verbsFor(opt.label ?? '');
      const busyText = opt.busyLabel ?? verbs.busy;
      const doneText = opt.doneLabel ?? verbs.done;
      flying.current = true;
      timers.current.forEach((x) => window.clearTimeout(x));
      timers.current = [];
      const showBusy = () => {
        if (!flying.current || !alive.current) return;
        setPhase('busy');
        setText(busyText);
        announce(busyText);
      };
      if (opt.graceMs) timers.current.push(window.setTimeout(showBusy, opt.graceMs));
      else showBusy();
      const still = window.setTimeout(() => {
        if (!flying.current || !alive.current) return;
        const s = say('busy1023.still', 'Still working…');
        setPhase('still');
        setText(s);
        announce(s);
      }, STILL_MS);
      timers.current.push(still);
      try {
        const r = await fn();
        window.clearTimeout(still);
        flying.current = false;
        if (alive.current) {
          const sd = typeof opt.showDone === 'function' ? opt.showDone() : opt.showDone;
          if (sd === false) {
            setPhase('idle');
            setText('');
          } else {
            setPhase('done');
            setText(doneText);
            timers.current.push(
              window.setTimeout(() => {
                if (!alive.current) return;
                setPhase('idle');
                setText('');
              }, DONE_MS),
            );
          }
        }
        if (opt.announceDone) announce(`${doneText}.`);
        return r;
      } catch (err) {
        window.clearTimeout(still);
        flying.current = false;
        if (alive.current) {
          setPhase('idle');
          setText('');
        }
        if (!(err instanceof PressRefused)) announce(say('busy1023.failed', 'Not done: {{why}}', { why: errorWords(err) }), 'assertive');
        return undefined;
      }
    },
    [announce],
  );

  const isFlying = useCallback(() => flying.current, []);
  return { phase, busy: phase === 'busy' || phase === 'still', text, run, flying: isFlying };
}
